/**
 * Turn-by-turn engine. Converts a stream of GPS fixes into navigation progress:
 * which step we're on, distance to the next maneuver that actually needs a
 * decision, which lanes to be in, and whether we've left the route.
 *
 * The important behaviour here is MANEUVER MERGING. Mapbox emits a step every
 * time its road model changes, so a highway run produces a chain of
 * "keep right to stay on 304" events. Announcing each one is noise and, worse,
 * it aimed the distance countdown at a non-event. We fold those runs together
 * (see significance.ts) and count down to the next real decision instead.
 *
 * Announcements are composed here rather than replayed from Mapbox, so that
 * every word is in the driver's chosen language (see phrasing.ts) and the lane
 * warning is spoken in the same breath as the turn.
 *
 * Deliberately has no DOM or map dependencies so its behaviour can be reasoned
 * about (and driven from a replayed track) in isolation.
 */

import type { BannerInstruction, Fix, Lane, NavProgress, Route, RouteStep } from '../core/types';
import type { UnitSystem } from '../core/settings';
import type { Lang } from '../core/i18n';
import { RouteIndex } from './routeIndex';
import { laneAdvice, laneVisibilityRange, resolveLanes, type LaneAdvice } from './lanes';
import { buildAnnouncement } from './phrasing';

/** Deviation beyond this, sustained, means we're off route. */
const OFF_ROUTE_METRES = 45;
/** Consecutive off-route fixes before we commit to rerouting. Rejects blips. */
const OFF_ROUTE_FIXES = 4;
/** Within this distance of the final waypoint we call it arrived. */
const ARRIVAL_METRES = 30;

export interface NavigatorConfig {
  units: UnitSystem;
  lang: Lang;
}

export interface NavigatorEvents {
  onProgress?: (p: NavProgress) => void;
  onAnnounce?: (text: string) => void;
  onOffRoute?: () => void;
  onArrive?: () => void;
  onStepChange?: (p: NavProgress) => void;
}

/** Announcement tiers, far to near. */
type Tier = 'far' | 'mid' | 'near' | 'now';

export class RouteNavigator {
  private index: RouteIndex;
  private lastVertex = 0;
  private currentStep = 0;
  private offRouteStreak = 0;
  private spoken = new Set<string>();
  /** Last sentence spoken, so two tiers that render alike don't repeat it. */
  private lastSpoken = '';
  private arrived = false;
  /** Distance along route at the previous fix, used to reject backwards jitter. */
  private lastAlong = 0;

  constructor(
    route: Route,
    private events: NavigatorEvents = {},
    private config: () => NavigatorConfig = () => ({ units: 'metric', lang: 'en' }),
  ) {
    this.index = new RouteIndex(route);
  }

  get route(): Route {
    return this.index.route;
  }

  /** Swap in a fresh route after a reroute, preserving nothing. */
  replaceRoute(route: Route): void {
    this.index = new RouteIndex(route);
    this.lastVertex = 0;
    this.currentStep = 0;
    this.offRouteStreak = 0;
    this.lastAlong = 0;
    this.arrived = false;
    this.spoken.clear();
    this.lastSpoken = '';
  }

  update(fix: Fix): NavProgress {
    const snapped = this.index.snap([fix.lng, fix.lat], this.lastVertex);
    this.lastVertex = snapped.segmentIndex;

    // GPS jitter can nudge us backwards; ignore small regressions so the
    // distance-to-turn readout never counts up while we're driving at it.
    let along = snapped.distanceAlongRoute;
    if (along < this.lastAlong && this.lastAlong - along < 25) {
      along = this.lastAlong;
    }
    this.lastAlong = along;

    const stepIdx = this.index.stepAt(along, this.currentStep);
    const stepChanged = stepIdx !== this.currentStep;
    this.currentStep = stepIdx;

    const current = this.index.steps[stepIdx];

    // The maneuver we're actually driving towards, skipping the lane-keeping
    // nudges that share our road number.
    const targetIdx = this.index.nextSignificant(stepIdx);
    const target = this.index.steps[targetIdx] ?? null;
    // The banner and lane data for a maneuver live on the step BEFORE it.
    const approach = this.index.steps[Math.max(0, targetIdx - 1)] ?? current;

    const maneuverAt = target ? target.startDistance : this.index.totalDistance;
    const distanceToManeuver = Math.max(0, maneuverAt - along);
    const distanceRemaining = Math.max(0, this.index.totalDistance - along);

    // Scale remaining time by how much of the route is left. Cruder than
    // re-requesting an ETA, but free and stable; the traffic-aware total from
    // the API is still the basis.
    const fraction = this.index.totalDistance > 0 ? distanceRemaining / this.index.totalDistance : 0;
    // Prefer the pace-adjusted total (the driver's own cruising speeds plus
    // red-light time) so the live ETA matches the one they chose the route on.
    const totalDuration = this.index.route._paceDuration ?? this.index.route.duration;
    const durationRemaining = totalDuration * fraction;

    // Off-route: require a run of bad fixes, and don't trip on a wide accuracy
    // circle (a 60 m accuracy fix is not evidence of leaving the road). While
    // stationary, GPS drift produces exactly this pattern - a parked car must
    // never trigger the reroute loop, so demand actual movement unless the
    // deviation is far beyond drift range.
    const tolerance = OFF_ROUTE_METRES + Math.min(40, Math.max(0, fix.accuracy - 10));
    const trustworthy = fix.accuracy <= 60;
    const moving = fix.speed == null || fix.speed > 1.5;
    if (snapped.deviation > tolerance && trustworthy && (moving || snapped.deviation > 150)) {
      this.offRouteStreak++;
    } else if (snapped.deviation <= tolerance) {
      this.offRouteStreak = 0;
    }
    const offRoute = this.offRouteStreak >= OFF_ROUTE_FIXES;

    const banner = pickBanner(approach.step.bannerInstructions, distanceToManeuver);
    const drivingSide = approach.step.driving_side ?? 'left';

    const speed = fix.speed ?? 0;
    const laneRange = laneVisibilityRange(speed);
    const rawLanes = distanceToManeuver <= laneRange ? resolveLanes(banner, approach.step) : null;
    // The maneuver's own modifier is passed in so laneAdvice can reject lane
    // data that contradicts the turn (see nav/lanes.ts).
    const advice = laneAdvice(rawLanes, drivingSide, target?.step.maneuver.modifier);

    const followIdx = target ? this.index.nextSignificant(targetIdx) : -1;
    const follow = followIdx > targetIdx ? (this.index.steps[followIdx] ?? null) : null;

    const progress: NavProgress = {
      legIndex: current.legIndex,
      stepIndex: current.stepIndex,
      distanceToManeuver,
      distanceRemaining,
      durationRemaining,
      step: current.step,
      maneuverStep: target?.step ?? null,
      nextStep: follow?.step ?? null,
      banner,
      lanes: rawLanes,
      laneAdvice: advice,
      drivingSide,
      snapped,
      offRoute,
    };

    if (target) {
      this.speakIfDue(target.step, targetIdx, distanceToManeuver, speed, advice, follow?.step ?? null);
    }

    if (stepChanged) this.events.onStepChange?.(progress);
    this.events.onProgress?.(progress);

    if (offRoute) {
      this.offRouteStreak = 0; // let the caller decide; don't spam
      this.events.onOffRoute?.();
    }

    if (!this.arrived && distanceRemaining <= ARRIVAL_METRES) {
      this.arrived = true;
      this.events.onArrive?.();
    }

    return progress;
  }

  /**
   * Announcement ladder. Distances scale with speed so the highway warning
   * arrives with time to change three lanes, while a town turn isn't
   * announced from half a kilometre away.
   */
  private speakIfDue(
    maneuverStep: RouteStep,
    targetIdx: number,
    distance: number,
    speedMs: number,
    advice: LaneAdvice | null,
    followStep: RouteStep | null,
  ): void {
    const tiers: Array<{ tier: Tier; at: number }> = [
      { tier: 'far', at: Math.max(1200, speedMs * 60) },
      { tier: 'mid', at: Math.max(400, speedMs * 22) },
      { tier: 'near', at: Math.max(120, speedMs * 9) },
      { tier: 'now', at: 45 },
    ];

    // Pick the most specific tier we've entered but not yet spoken.
    let due: Tier | null = null;
    for (const { tier, at } of tiers) {
      if (distance <= at && !this.spoken.has(`${targetIdx}:${tier}`)) due = tier;
    }
    if (!due) return;

    // Entering a tier implies every coarser one is moot - mark them spoken so
    // resuming mid-approach doesn't replay a backlog of "in 1 kilometre...".
    for (const { tier } of tiers) {
      this.spoken.add(`${targetIdx}:${tier}`);
      if (tier === due) break;
    }

    // Don't stack a "far" warning immediately on top of the previous maneuver:
    // only worth it when the run is long enough to be worth pre-warning.
    const runLength = this.runLengthTo(targetIdx);
    if (due === 'far' && runLength < Math.max(1800, speedMs * 90)) return;
    if (due === 'mid' && runLength < 250) return;

    const { units, lang } = this.config();
    const trap = advice?.trap != null;
    const text = buildAnnouncement({
      maneuverStep,
      distance,
      units,
      lang,
      advice,
      // Lane guidance from the mid tier onwards when it's a trap, otherwise
      // once we're close enough to act on it.
      withLanes: due === 'near' || due === 'now' || (due === 'mid' && trap),
      // Only preview the follow-on when it lands right after this one.
      thenStep: due === 'now' || due === 'near' ? this.closeFollow(targetIdx, followStep) : null,
    });

    // Close to the maneuver the "near" and "now" tiers render identically
    // (both drop the distance prefix). Saying it twice sounds like a stutter.
    if (text === this.lastSpoken) return;
    this.lastSpoken = text;

    this.events.onAnnounce?.(text);
  }

  /** Distance from the previous significant maneuver to this one. */
  private runLengthTo(targetIdx: number): number {
    const target = this.index.steps[targetIdx];
    if (!target) return 0;
    const prev = this.index.previousSignificant(targetIdx);
    const from = prev >= 0 ? this.index.steps[prev].startDistance : 0;
    return Math.max(0, target.startDistance - from);
  }

  /** The follow-on maneuver, but only if it arrives soon enough to matter. */
  private closeFollow(targetIdx: number, followStep: RouteStep | null): RouteStep | null {
    if (!followStep) return null;
    const followIdx = this.index.nextSignificant(targetIdx);
    const a = this.index.steps[targetIdx];
    const b = this.index.steps[followIdx];
    if (!a || !b) return null;
    return b.startDistance - a.startDistance <= 500 ? followStep : null;
  }
}

/**
 * Choose the banner in force. Mapbox gives several per step, each with the
 * distance at which it should *start* showing; the correct one is the most
 * specific (smallest trigger distance) that we're already inside.
 */
export function pickBanner(
  banners: BannerInstruction[] | undefined,
  distanceToManeuver: number,
): BannerInstruction | null {
  if (!banners?.length) return null;
  let best: BannerInstruction | null = null;
  for (const b of banners) {
    if (distanceToManeuver <= b.distanceAlongGeometry) {
      if (!best || b.distanceAlongGeometry < best.distanceAlongGeometry) best = b;
    }
  }
  return best ?? banners.reduce((a, b) => (a.distanceAlongGeometry > b.distanceAlongGeometry ? a : b));
}

/** Re-exported for the UI, which renders whatever the navigator resolved. */
export type { Lane };
export { OFF_ROUTE_METRES, ARRIVAL_METRES };
